import remarkParse from 'remark-parse';
import { unified } from 'unified';
import { visit } from 'unist-util-visit';

/**
 * A skill chip serializes to an inline markdown link, `[name](skill:<skillId>/<versionId>)`,
 * so it keeps its place in the text. The chat sends the pinned ids beside the prompt; a task
 * description has only its text, so the server reads the skills from these links.
 */
const SKILL_LINK_SCHEME = 'skill:';
const SKILL_LINK_URL_PATTERN = /^skill:([^/]+)\/([^/]+)$/;
const SKILL_MARKDOWN_PARSER = unified().use(remarkParse);

export interface SkillLinkReference {
  skillId: string;
  versionId: string;
}

export function buildSkillLinkUrl({ skillId, versionId }: SkillLinkReference) {
  return `${getSkillLinkUrlPrefix(skillId)}${encodeURIComponent(versionId)}`;
}

/** Everything before the version in a skill link, to find any version of this skill. */
export function getSkillLinkUrlPrefix(skillId: string) {
  return `${SKILL_LINK_SCHEME}${encodeURIComponent(skillId)}/`;
}

/** The ids a `skill:` link carries, or null when the url is not a well-formed skill link. */
export function parseSkillLinkUrl(url: string): SkillLinkReference | null {
  const match = SKILL_LINK_URL_PATTERN.exec(url);
  if (!match) return null;
  try {
    return { skillId: decodeURIComponent(match[1]), versionId: decodeURIComponent(match[2]) };
  } catch {
    return null;
  }
}

/** Actual Markdown links, in document order, keeping the first link to each skill. */
export function getSkillLinksFromMarkdown(markdown: string): SkillLinkReference[] {
  if (!markdown.includes(SKILL_LINK_SCHEME)) return [];
  // A link needs a closing destination, a reference definition, or an autolink delimiter.
  if (!markdown.includes(')') && !markdown.includes(']:') && !markdown.includes('>')) return [];
  const tree = SKILL_MARKDOWN_PARSER.parse(markdown);
  const definitions = new Map<string, string>();
  visit(tree, 'definition', node => {
    const id = node.identifier.toLowerCase();
    if (!definitions.has(id)) definitions.set(id, node.url);
  });
  const seenSkillIds = new Set<string>();
  const links: SkillLinkReference[] = [];
  visit(tree, node => {
    let url: string | undefined;
    if (node.type === 'link') url = node.url;
    else if (node.type === 'linkReference') url = definitions.get(node.identifier.toLowerCase());
    if (!url) return;

    const link = parseSkillLinkUrl(url);
    if (!link || seenSkillIds.has(link.skillId)) return;
    seenSkillIds.add(link.skillId);
    links.push(link);
  });
  return links;
}
